import {
  AiAssistantChat,
  AiAssistantSavedChat,
} from "@/components/ai-assistant/AiAssistantChat.tsx";
import {
  AI_ASSISTANT_PANEL_WIDTH,
  clampAiAssistantPanelWidth,
} from "@/components/ai-assistant/ai-assistant-panel-width.ts";
import { AiAssistantThreads } from "@/components/ai-assistant/AiAssistantThreads.tsx";
import { useEmployeeAppConfigStore } from "@/stores/employee.store.ts";
import {
  ButtonIcon,
  FontAwesomeIcon,
  sidebarTheme,
  Stack,
  Text,
  useResizeDrag,
} from "@skailer-com/uikit";
import { generateId } from "ai";
import { CSSProperties, KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

type Conversation =
  | { key: string; isSaved: false; threadId?: number }
  | { key: string; isSaved: true; threadId: number };

const newConversation = (): Conversation => ({ key: generateId(), isSaved: false });

interface AiAssistantPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AiAssistantPanel({ isOpen, onClose }: AiAssistantPanelProps) {
  const { t } = useTranslation();
  const [conversation, setConversation] = useState(newConversation);
  const [isThreadsOpen, setIsThreadsOpen] = useState(false);
  const { aiAssistantPanelWidth, setAiAssistantPanelWidth } = useEmployeeAppConfigStore();
  const { dragWidth, isResizing, startResize } = useResizeDrag();
  const width = dragWidth ?? clampAiAssistantPanelWidth(aiAssistantPanelWidth);

  const handleSeparatorKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    setAiAssistantPanelWidth(
      clampAiAssistantPanelWidth(width - step * AI_ASSISTANT_PANEL_WIDTH.step)
    );
  };

  const startConversation = () => {
    setIsThreadsOpen(false);
    setConversation(newConversation());
  };

  const openThread = (threadId: number) => {
    setIsThreadsOpen(false);
    if (threadId !== conversation.threadId) {
      setConversation({ key: generateId(), isSaved: true, threadId });
    }
  };

  const handleThreadDeleted = (threadId: number) => {
    if (threadId === conversation.threadId) setConversation(newConversation());
  };

  // Ход покинутого разговора дописывается в фоне: его диалог не должен стать диалогом текущего.
  const { key } = conversation;
  const handleThreadSaved = (threadId: number) =>
    setConversation((current) => (current.key === key ? { ...current, threadId } : current));

  return (
    <aside
      aria-label={t("ai:assistant")}
      className={twMerge(
        "fixed inset-0 z-overlay flex flex-col bg-light print:hidden",
        "md:left-auto md:w-(--ai-assistant-panel-width) md:border-l md:border-dark/5 md:shadow-md",
        "xl:relative xl:inset-auto xl:z-auto xl:h-full xl:shrink-0 xl:shadow-none",
        !isOpen && "hidden"
      )}
      style={
        {
          "--ai-assistant-panel-width": `${width}px`,
          backgroundImage:
            "linear-gradient(180deg, hsl(var(--var-color-background-gradient-from)) 0%, hsl(var(--var-color-background-gradient-to)) 78.17%)",
        } as CSSProperties
      }
    >
      {isOpen && (
        <div
          role={"separator"}
          tabIndex={0}
          aria-label={t("ai:assistant_resize")}
          aria-orientation={"vertical"}
          aria-valuemin={AI_ASSISTANT_PANEL_WIDTH.min}
          aria-valuemax={AI_ASSISTANT_PANEL_WIDTH.max}
          aria-valuenow={width}
          className={twMerge(
            sidebarTheme.layout.separator,
            isResizing && sidebarTheme.layout.separatorActive,
            "absolute inset-y-0 -left-1 mx-0 hidden md:block"
          )}
          onPointerDown={(event) =>
            startResize(event, {
              width,
              clamp: clampAiAssistantPanelWidth,
              // Разделитель на левом краю: движение курсора вправо сужает панель
              pxPerUnit: -1,
              onEnd: setAiAssistantPanelWidth,
            })
          }
          onKeyDown={handleSeparatorKeyDown}
        />
      )}
      <div className={"flex flex-1 min-h-0 flex-col bg-light/60"}>
        <Stack
          direction={"horizontal"}
          items={"center"}
          justify={"between"}
          className={"px-6 pt-5 pb-3 shrink-0"}
        >
          <Text className={"text-lg font-semibold"}>
            {isThreadsOpen ? t("ai:assistant_threads") : t("ai:assistant_title")}
          </Text>
          <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
            <ButtonIcon
              variant={"plain"}
              aria-label={t("ai:assistant_threads")}
              aria-pressed={isThreadsOpen}
              icon={<FontAwesomeIcon icon={"fa-light fa-clock-rotate-left"} />}
              onClick={() => setIsThreadsOpen((open) => !open)}
            />
            <ButtonIcon
              variant={"plain"}
              aria-label={t("ai:assistant_thread_new")}
              icon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
              onClick={startConversation}
            />
            <ButtonIcon
              variant={"plain"}
              aria-label={t("common:close")}
              icon={<FontAwesomeIcon icon={"fa-light fa-xmark"} />}
              onClick={onClose}
            />
          </Stack>
        </Stack>

        {isThreadsOpen && (
          <div className={"flex-1 min-h-0 overflow-y-auto px-6 py-4"}>
            <AiAssistantThreads
              activeThreadId={conversation.threadId}
              onOpen={openThread}
              onDeleted={handleThreadDeleted}
            />
          </div>
        )}

        <div className={twMerge("flex flex-1 min-h-0 flex-col", isThreadsOpen && "hidden")}>
          {conversation.isSaved ? (
            <AiAssistantSavedChat
              key={key}
              isOpen={isOpen}
              threadId={conversation.threadId}
              onThreadSaved={handleThreadSaved}
            />
          ) : (
            <AiAssistantChat
              key={key}
              isOpen={isOpen}
              initialMessages={[]}
              onThreadSaved={handleThreadSaved}
            />
          )}
        </div>
      </div>
    </aside>
  );
}
